import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs/Subscription';

import { Constants, ItsrApiService, MixpanelService, SecurityQuestionListResponse } from "../_services";
import { MixpanelTrackedFormComponent, matchEmailValidator, matchPasswordValidator } from '../global';

@Component({
  templateUrl: 'activate.component.html'
})
export class ActivateComponent extends MixpanelTrackedFormComponent implements OnInit, OnDestroy {
  private readonly passwordChangeSubscription: Subscription;
  private readonly emailChangeSubscription: Subscription;
  data: any = null;
  formFields: any = null;
  emailFormGroup: FormGroup;
  passwordFormGroup: FormGroup;
  showLoadingIcon: boolean = false;
  formSuccess: boolean | null = null;
  displayError: string = '';
  securityQuestionList: SecurityQuestionListResponse = [];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private itsrApiService: ItsrApiService,
    private titleService: Title,
    formBuilder: FormBuilder,
    mixpanelService: MixpanelService
  ) {
    super(
      formBuilder.group({
        subscriptionNumber: [null, Validators.compose([
          Validators.required,
          Validators.pattern(Constants.RegEx.SubscriptionNumber)
        ])],

        firstName: [null, Validators.compose([
          Validators.required,
          Validators.pattern(Constants.RegEx.FirstName)
        ])],

        lastName: [null, Validators.compose([
          Validators.required,
          Validators.pattern(Constants.RegEx.LastName)
        ])],

        phoneNumber: [null, Validators.compose([
          Validators.required,
          Validators.pattern(Constants.RegEx.PhoneNumber)
        ])],

        username: [null, Validators.compose([
          Validators.required,
          Validators.minLength(5), Validators.maxLength(12),
          Validators.pattern(Constants.RegEx.UserName)
        ])],

        securityQuestion: [0, Validators.compose([Validators.required])],
        securityAnswer: [null, Validators.compose([Validators.required, Validators.maxLength(25)])],

        marketingOptIn: false,
      }),
      mixpanelService
    );

    this.passwordFormGroup = formBuilder.group({
      password: [null, Validators.compose([
        Validators.required,
        Validators.minLength(8), Validators.maxLength(12),
        Validators.pattern(Constants.RegEx.Password)
      ])],
      confirmPassword: [null]
    });

    this.passwordFormGroup.get('confirmPassword').setValidators([
      Validators.required,
      matchPasswordValidator(this.passwordFormGroup.get('password'))
    ]);

    this.passwordChangeSubscription = this.passwordFormGroup.get('password').valueChanges.subscribe((): void => {
      this.passwordFormGroup.get('confirmPassword').updateValueAndValidity();
    });

    this.emailFormGroup = formBuilder.group({
      email: [null, Validators.compose([
        Validators.required,
        Validators.maxLength(60),
        Validators.pattern(Constants.RegEx.Email)
      ])],
      confirmEmail: [null],
    });

    this.emailFormGroup.get('confirmEmail').setValidators([
      Validators.required,
      matchEmailValidator(this.emailFormGroup.get('email'))
    ]);

    this.emailChangeSubscription = this.emailFormGroup.get('email').valueChanges.subscribe((): void => {
      this.emailFormGroup.get('confirmEmail').updateValueAndValidity();
    });
  }

  ngOnInit(): void {
    this.data = this.route.snapshot.data;
    this.formFields = this.data.content.form.fields;

    this.mixpanelService.trackPageView(this.data.content.windowTitle);
    // track time elapsed (in seconds) between landing on the page and a successful form submission
    this.mixpanelService.timeEvent(MixpanelService.EventNameAccountActivation);

    this.titleService.setTitle(this.data.content.windowTitle);

    this.form.get('securityQuestion').patchValue('0');
    const subscription = this.itsrApiService.getSecurityQuestionList()
      .subscribe(
        (response): void => {
          if (!ItsrApiService.isErrorResponse(response)) {
            this.securityQuestionList = response.body;
            this.form.get('securityQuestion').patchValue(String(this.securityQuestionList[0].ID));
            return;
          }
        },
        undefined, // error handler
        (): void => { subscription.unsubscribe(); });
  }

  ngOnDestroy(): void {
    this.passwordChangeSubscription.unsubscribe();
    this.emailChangeSubscription.unsubscribe();
  }

  submit(event: Event): void {
    event.preventDefault();

    let isFormInvalid: boolean = false;

    if (!this.isFormGroupValid()) {
      isFormInvalid = true;
      this.markAllFormFieldsTouched();
    }

    if (!this.isFormGroupValid(this.emailFormGroup)) {
      isFormInvalid = true;
      this.markAllFormFieldsTouched(this.emailFormGroup);
    }

    if (!this.isFormGroupValid(this.passwordFormGroup)) {
      isFormInvalid = true;
      this.markAllFormFieldsTouched(this.passwordFormGroup);
    }

    if (isFormInvalid) {
      this.setFocusOnFirstInvalidField();
      return;
    }

    this.showLoadingIcon = true;
    const requestBody = this.buildRequestBody();
    const subscription = this.itsrApiService.postActivateUser(requestBody)
      .subscribe(
        (response): void => {
          if (ItsrApiService.isErrorResponse(response)) {
            this.formSuccess = false;
            this.displayError = response.Errors[0].Message;
            return;
          }

          // associate the just-activated registration number with the
          // Mixpanel-generated distinct_id of the visitor
          this.mixpanelService.alias(requestBody.subscriptionNumber);
          this.mixpanelTrackActivate({
            [MixpanelService.PropertyNameInitialMarketingOptIn]: String(requestBody.marketingOptIn)
          });

          this.router.navigate(['/login'], { queryParams: { activated: 1 } });
        },
        undefined, // error handler
        (): void => {
          this.showLoadingIcon = false;
          subscription.unsubscribe();
        });
  }

  private buildRequestBody() {
    const value = this.form.value;
    const valueEmail = this.emailFormGroup.value;
    const valuePassword = this.passwordFormGroup.value;

    const phoneNumDigitsOnly: string[] | null = (value.phoneNumber as string).match(/\d+/g);
    const securityQuestion = this.securityQuestionList
      .filter(question => question.ID === +this.form.get('securityQuestion').value);

    return {
      subscriptionNumber: value.subscriptionNumber,
      firstName: value.firstName,
      lastName: value.lastName,
      phoneNumber: phoneNumDigitsOnly && phoneNumDigitsOnly.join(''),
      password: valuePassword.password,
      confirmPassword: valuePassword.confirmPassword,
      // ++
      // API has a defect pending which causes a failure if emails have same values but different letter-casing.
      // for now, we convert both emails to lowercase for the API call to succeed
      //
      email: (valueEmail.email as string).toLowerCase(),
      confirmedEmail: (valueEmail.confirmEmail as string).toLowerCase(),
      //
      // --
      username: value.username,
      securityQuestionId: securityQuestion[0].ID,
      securityQuestion: securityQuestion[0].Question,
      securityAnswer: value.securityAnswer,
      marketingOptIn: value.marketingOptIn
    };
  }

  private mixpanelTrackActivate(props: { [key: string]: string }): void {
    this.mixpanelService.track(MixpanelService.EventNameAccountActivation, props);
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/activate.component.ts